import * as THREE from 'three';
import {
  TRAFFIC_LIGHT_ICON_MAX,
  TRAFFIC_LIGHT_LABEL_CANVAS_W,
  TRAFFIC_LIGHT_LABEL_CANVAS_H,
  TRAFFIC_LIGHT_LABEL_Z_OFFSET,
  trafficLightTypeColorMap,
} from './styleConfig';
import { getLabelDpr } from './util';
import { TRAFFIC_LIGHT_LABEL_DPR_MAX } from './constant';
import { TrafficLightMeta, TrafficLightColorType } from './types';

/**
 * 红绿灯 ID 标签池：双池
 * - activeLabels: Map<id, Sprite> 当前正在显示的标签
 * - freeLabels: Sprite[] 空闲待分配的标签
 */
class TrafficLightLabelPool {
  // 场景引用
  private scene: THREE.Scene;
  // 标签容器，所有标签都放在这个 group 里
  private group: THREE.Group;
  // 当前正在显示的标签：红绿灯id -> Sprite
  private activeLabels: Map<string, THREE.Sprite> = new Map();
  // 空闲待分配的标签池
  private freeLabels: THREE.Sprite[] = [];
  // 当 activeLabels 过多且 freeLabels 为空时，允许的最大老化帧数（超过这个帧数未更新的标签会被回收）
  private releaseAfterFrames: number = 3;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // 所有标签放在一个 group 里，方便统一设置位置（根据车辆坐标系调整整体偏移）
    this.group = new THREE.Group();
    this.group.frustumCulled = false;
    this.scene.add(this.group);

    const labelDpr = getLabelDpr(TRAFFIC_LIGHT_LABEL_DPR_MAX);

    // 预创建一批 Sprite，初始状态都不可见，放在 freeLabels 池里
    for (let i = 0; i < TRAFFIC_LIGHT_ICON_MAX; i++) {
      const sprite = this.createSprite(labelDpr);

      // 放入group
      this.group.add(sprite);

      // 初始状态下放入空闲池
      this.freeLabels.push(sprite);
    }
  }

  /**
   * 创建sprite
   *
   * @private
   * @param {number} labelDpr
   * @return {*}  {THREE.Sprite}
   * @memberof TrafficLightLabelPool
   */
  private createSprite(labelDpr: number): THREE.Sprite {
    const canvas = document.createElement('canvas');

    // 根据 DPR 设置 canvas 尺寸，确保文本清晰。
    // 实际绘制时会使用 CSS 像素坐标（logicalW/logicalH），但纹理分辨率是物理像素（乘以 DPR）。
    canvas.width = Math.round(TRAFFIC_LIGHT_LABEL_CANVAS_W * labelDpr);
    canvas.height = Math.round(TRAFFIC_LIGHT_LABEL_CANVAS_H * labelDpr);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    // 设置坐标变换，后续绘制使用逻辑像素坐标（0~CANVAS_W/H），由 DPR 控制实际分辨率
    ctx.setTransform(labelDpr, 0, 0, labelDpr, 0, 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 尝试开启高质量文本渲染（部分浏览器可能不支持）
    try {
      (ctx as any).imageSmoothingEnabled = true;
      (ctx as any).imageSmoothingQuality = 'high';
    } catch (_) {}

    // 初始清空画布
    ctx.clearRect(
      0,
      0,
      TRAFFIC_LIGHT_LABEL_CANVAS_W,
      TRAFFIC_LIGHT_LABEL_CANVAS_H
    );

    // 创建纹理
    const tex = new THREE.CanvasTexture(canvas);
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.premultiplyAlpha = true;
    tex.needsUpdate = true;

    // 创建 SpriteMaterial
    const mat = new THREE.SpriteMaterial({
      sizeAttenuation: false,
      map: tex,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    // 创建 Sprite
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.15, 0.15, 0.15);
    sprite.renderOrder = 1000;
    sprite.visible = false;

    // 把自己要用到的数据都挂在 userData 上，方便后续更新时访问
    (sprite as any).userData = {
      canvas,
      ctx,
      tex,
      dpr: labelDpr,
      logicalW: TRAFFIC_LIGHT_LABEL_CANVAS_W,
      logicalH: TRAFFIC_LIGHT_LABEL_CANVAS_H,
      lastText: null,
      lastColor: null,
      assignedId: null,
      lastSeenFrame: -1,
    };

    return sprite;
  }

  /**
   * 更新文本显示
   *
   * @param {TrafficLightMeta[]} lights
   * @param {number} frameCounter
   * @memberof TrafficLightLabelPool
   */
  update(lights: TrafficLightMeta[], frameCounter: number) {
    const currentIds = new Set<string>();

    // 遍历当前帧的红绿灯数据，更新或分配文本
    for (let i = 0; i < lights.length; i++) {
      const light = lights[i];
      if (!light || !light.position) continue;

      const idText = String(light.id || '');
      currentIds.add(idText);

      let sprite = this.activeLabels.get(idText);
      if (!sprite) {
        // 没有可用的标签了，尝试回收一些老化的标签
        if (this.freeLabels.length === 0) {
          for (const [oldId, oldSp] of this.activeLabels.entries()) {
            const last = (oldSp as any).userData.lastSeenFrame || -1;
            if (frameCounter - last > this.releaseAfterFrames) {
              this.activeLabels.delete(oldId);
              this.resetSprite(oldSp);
              this.freeLabels.push(oldSp);
              break;
            }
          }
        }
        // 如果回收后仍然没有空闲标签，就只能跳过这个红绿灯的显示了
        if (this.freeLabels.length === 0) continue;

        // 从空闲池里拿一个标签来显示这个红绿灯
        sprite = this.freeLabels.pop()!;

        // 分配给这个红绿灯 ID，并记录在 userData 里
        (sprite as any).userData.assignedId = idText;
        (sprite as any).userData.lastText = null;
        (sprite as any).userData.lastColor = null;
        // 加入 activeLabels
        this.activeLabels.set(idText, sprite);
      }

      // 更新最后看到这个标签的帧数
      (sprite as any).userData.lastSeenFrame = frameCounter;

      // 标签放在 icon 正上方
      const lx = Number(light.position.x) || 0;
      const ly = (Number(light.position.y) || 0) + 1;
      sprite.position.set(lx, ly, 0);

      const ud = (sprite as any).userData || {};

      // 尝试获取世界坐标位置，后续 layout 时使用这个位置来计算屏幕坐标
      try {
        const anchorWorld = new THREE.Vector3();
        sprite.getWorldPosition(anchorWorld);
        ud.anchorWorld = anchorWorld;
      } catch (_) {}

      // 只有在文本内容或颜色发生变化时才重新绘制，避免不必要的性能开销
      const color =
        trafficLightTypeColorMap[light.colorType || TrafficLightColorType.GREY];
      if (ud.lastText !== idText || ud.lastColor !== color) {
        const canvas = ud.canvas as HTMLCanvasElement;
        const ctx = ud.ctx as CanvasRenderingContext2D;
        if (ctx && canvas) {
          // 设置坐标变换，后续绘制使用逻辑像素坐标（0~logicalW/logicalH）
          const logicalW = Number(ud.logicalW) || TRAFFIC_LIGHT_LABEL_CANVAS_W;
          const logicalH = Number(ud.logicalH) || TRAFFIC_LIGHT_LABEL_CANVAS_H;
          const dpr = Number(ud.dpr) || 1;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

          // 清空画布
          ctx.clearRect(0, 0, logicalW, logicalH);

          // 根据文本内容设置字体大小和线宽，确保不同长度的文本都能清晰显示
          const fontSize = 48;
          ctx.font = `600 ${fontSize}px "Arial Black", "Arial Rounded MT Bold", "Helvetica Neue", "PingFang SC", "Microsoft YaHei", sans-serif`;
          ctx.lineWidth = Math.max(1, Math.round(fontSize / 18)) / dpr;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = color;

          // 文本绘制在画布中心
          const y = logicalH / 2;
          const x = logicalW / 2;
          const text = String(idText || '');
          ctx.fillText(text, x, y);

          // 如果需要描边以增强对比度，可以在这里添加描边逻辑，例如：
          if (ud.tex) ud.tex.needsUpdate = true;
          ud.lastText = idText;
          ud.lastColor = color;
        }
      }

      // 确保这个标签可见
      sprite.visible = true;
    }

    // 回收：如果 activeLabels.size > lights.length 且某些 sprite 老化，直接移到 freeLabels
    if (this.activeLabels.size > lights.length) {
      for (const [id, sp] of this.activeLabels.entries()) {
        if (currentIds.has(id)) continue;
        const ud = (sp as any).userData || {};
        const last =
          typeof ud.lastSeenFrame === 'number' ? ud.lastSeenFrame : -1;
        if (frameCounter - last > this.releaseAfterFrames) {
          this.activeLabels.delete(id);
          this.resetSprite(sp);
          this.freeLabels.push(sp);
        }
      }
    }
  }

  /**
   * 重置sprite的属性
   *
   * @private
   * @param {THREE.Sprite} sp
   * @memberof TrafficLightLabelPool
   */
  private resetSprite(sp: THREE.Sprite) {
    sp.visible = false;
    const ud = (sp as any).userData || {};
    ud.anchorWorld = undefined;
    ud.lastText = null;
    ud.lastColor = null;
    ud.assignedId = null;
    ud.lastSeenFrame = -1;
  }

  /**
   * 偏移文本的位置，防止多个文本交叠
   *
   * @param {(THREE.Camera | null)} camera
   * @param {{ domElement: { clientWidth: number; clientHeight: number } }} [renderer]
   * @return {*}
   * @memberof TrafficLightLabelPool
   */
  layout(
    camera: THREE.Camera | null,
    renderer?: { domElement: { clientWidth: number; clientHeight: number } }
  ) {
    try {
      if (!camera || !renderer) return;
      const dom = renderer.domElement;
      const width = dom.clientWidth || 1;
      const height = dom.clientHeight || 1;

      const visible: Array<any> = [];
      const tmpV = new THREE.Vector3();

      for (const sprite of this.activeLabels.values()) {
        if (!sprite.visible) continue;
        const ud: any = (sprite as any).userData || {};

        let anchorWorld: THREE.Vector3 | null = null;
        if (ud && ud.anchorWorld) {
          anchorWorld = (ud.anchorWorld as THREE.Vector3).clone();
        } else {
          sprite.getWorldPosition(tmpV);
          anchorWorld = tmpV.clone();
          if (ud) ud.anchorWorld = anchorWorld;
        }

        // 把世界坐标转换到屏幕坐标，判断是否在视锥内（z在-1~1之间），以及屏幕位置
        const ndc = anchorWorld.clone().project(camera);
        if (ndc.z < -1 || ndc.z > 1) continue;
        const px = (ndc.x + 1) * 0.5 * width;
        const py = (1 - ndc.y) * 0.5 * height;

        visible.push({ sprite, anchorWorld, ndc, px, py });
      }

      // 如果只有一个或没有重叠的标签，直接把它们放在对应位置，不需要调整
      if (visible.length <= 1) {
        for (const it of visible) {
          const local = this.group.worldToLocal(it.anchorWorld.clone());
          it.sprite.position.copy(local);
        }
        return;
      }

      // 对于重叠的标签，根据它们的屏幕位置进行分组（距离小于某个阈值的认为是重叠）
      // 然后在每个组内根据屏幕 y 坐标排序，均匀分布在一个范围内，避免完全重叠在一起。
      const used = new Array<boolean>(visible.length).fill(false);
      const threshold = 40;
      const gap = 26;

      for (let i = 0; i < visible.length; i++) {
        if (used[i]) continue;
        const base = visible[i];
        const group = [i];
        used[i] = true;
        // 把距离 base 的屏幕位置小于 threshold 的标签都归为一组
        for (let j = i + 1; j < visible.length; j++) {
          if (used[j]) continue;
          const dx = visible[j].px - base.px;
          const dy = visible[j].py - base.py;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < threshold) {
            group.push(j);
            used[j] = true;
          }
        }

        // 如果组内只有一个标签，直接放在对应位置；如果有多个标签，根据它们的屏幕 y 坐标排序，均匀分布在一个范围内
        if (group.length === 1) {
          const it = visible[group[0]];
          const local = this.group.worldToLocal(it.anchorWorld.clone());
          it.sprite.position.copy(local);
          continue;
        }

        // 根据屏幕 y 坐标排序
        group.sort((a, b) => visible[a].py - visible[b].py);
        const mid = (group.length - 1) / 2;

        // 均匀分布在以 base.py 为中心，间隔为 gap 的范围内
        // 修复：使用 base.py 作为展开中心点，避免标签位置被压缩和顺序反转
        for (let k = 0; k < group.length; k++) {
          const idx = group[k];
          const it = visible[idx];
          const offset = (k - mid) * gap;
          const targetPy = base.py + offset;

          const ndcX = it.ndc.x;
          const ndcZ = it.ndc.z;
          const ndcY = 1 - (targetPy / height) * 2;

          // 把调整后的屏幕坐标转换回世界坐标，设置标签位置
          const v = new THREE.Vector3(ndcX, ndcY, ndcZ).unproject(camera);
          const local = this.group.worldToLocal(v);
          it.sprite.position.copy(local);
        }
      }
    } catch (err) {
      console.warn('layoutTrafficLightLabels failed', err);
    }
  }

  /**
   * 更新位置
   *
   * @param {number} x
   * @param {number} y
   * @memberof TrafficLightLabelPool
   */
  setOrigin(x: number, y: number) {
    this.group.position.set(x, y, 0);
  }

  /**
   * 释放池内所有资源
   *
   * @memberof TrafficLightLabelPool
   */
  dispose() {
    // 从场景中移除 group
    if (this.group.parent) {
      this.group.parent.remove(this.group);
    }

    // 释放所有 sprite 的材质和纹理
    this.group.children.forEach((sprite: any) => {
      if (sprite.material) {
        if (sprite.material.map) {
          sprite.material.map.dispose();
        }
        sprite.material.dispose();
      }
      const ud = sprite.userData || {};
      if (ud.tex) ud.tex.dispose();
    });

    // 清空池
    this.activeLabels.clear();
    this.freeLabels = [];
  }
}

export default TrafficLightLabelPool;
